interface ActivityManager {
  startMonitoring: () => void;
  stopMonitoring: () => void;
  recordActivity: (activityType: string) => void;
  isActive: () => boolean;
}

class ActivityService implements ActivityManager {
  private lastActivityTime: number = Date.now();
  private activityTimeout: NodeJS.Timeout | null = null;
  private backgroundServices: Map<string, NodeJS.Timeout> = new Map();
  private isMonitoring: boolean = false;
  private readonly INACTIVE_THRESHOLD = 2 * 60 * 1000;
  private readonly CHECK_INTERVAL = 30 * 1000;


  startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.lastActivityTime = Date.now();
    
    console.log('Activity monitoring started');
    
    //Inactivity check
    this.activityTimeout = setInterval(() => {
      this.checkActivityStatus();
    }, this.CHECK_INTERVAL);
    
    //Start background services
    this.startBackgroundServices();
    
    //Listen for activity
    this.attachActivityListeners();
  }

  stopMonitoring() {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    
    if (this.activityTimeout) {
      clearInterval(this.activityTimeout);
      this.activityTimeout = null;
    }
    
    this.stopBackgroundServices();
    this.removeActivityListeners();
    
    console.log('Activity monitoring stopped');
  }

  recordActivity(activityType: string) {
    const now = Date.now();
    const wasInactive = !this.isActive();
    
    this.lastActivityTime = now;
    
    //Restart services on activity
    if (wasInactive && this.isMonitoring) {
      console.log(`Activity detected (${activityType}) - restarting background services`);
      this.startBackgroundServices();
    }
    
    //ToDo: Remove log in production
    if (activityType !== 'mouse' && activityType !== 'scroll') {
      console.log(`📱 User activity: ${activityType}`);
    }
  }

  isActive(): boolean {
    return (Date.now() - this.lastActivityTime) < this.INACTIVE_THRESHOLD;
  }

  private checkActivityStatus() {
    const inactive = !this.isActive();
    const hasActiveServices = this.backgroundServices.size > 0;
    
    if (inactive && hasActiveServices) {
      const inactiveTime = Math.round((Date.now() - this.lastActivityTime) / 1000);
      console.log(`No activity for ${inactiveTime}s - stopping background services`);
      this.stopBackgroundServices();
    }
  }

  private startBackgroundServices() {
  this.stopBackgroundServices();
  }

  private stopBackgroundServices() {
    this.backgroundServices.forEach((interval, serviceName) => {
      clearInterval(interval);
      console.log(`Stopped background service: ${serviceName}`);
    });
    this.backgroundServices.clear();
  }

  private attachActivityListeners() {
    //Mouse movement
    let mouseThrottle = false;
    const mouseHandler = () => {
      if (!mouseThrottle) {
        this.recordActivity('mouse');
        mouseThrottle = true;
        setTimeout(() => mouseThrottle = false, 10000);
      }
    };

    //Keyboard activity
    const keyHandler = () => this.recordActivity('keyboard');

    //Page visibility changes
    const visibilityHandler = () => {
      if (!document.hidden) {
        this.recordActivity('visibility');
      }
    };

    //Scroll activity
    let scrollThrottle = false;
    const scrollHandler = () => {
      if (!scrollThrottle) {
        this.recordActivity('scroll');
        scrollThrottle = true;
        setTimeout(() => scrollThrottle = false, 5000);
      }
    };

    //Touch events for mobile, eventually
    const touchHandler = () => this.recordActivity('touch');

    //Event listeners
    document.addEventListener('mousemove', mouseHandler, { passive: true });
    document.addEventListener('keydown', keyHandler, { passive: true });
    document.addEventListener('click', () => this.recordActivity('click'), { passive: true });
    document.addEventListener('scroll', scrollHandler, { passive: true });
    document.addEventListener('touchstart', touchHandler, { passive: true });
    document.addEventListener('visibilitychange', visibilityHandler);

    //Store refs for cleanup
    (this as any).activityListeners = {
      mouseHandler,
      keyHandler,
      visibilityHandler,
      scrollHandler,
      touchHandler,
      clickHandler: () => this.recordActivity('click')
    };
  }

  private removeActivityListeners() {
    const listeners = (this as any).activityListeners;
    if (!listeners) return;

    document.removeEventListener('mousemove', listeners.mouseHandler);
    document.removeEventListener('keydown', listeners.keyHandler);
    document.removeEventListener('click', listeners.clickHandler);
    document.removeEventListener('scroll', listeners.scrollHandler);
    document.removeEventListener('touchstart', listeners.touchHandler);
    document.removeEventListener('visibilitychange', listeners.visibilityHandler);

    delete (this as any).activityListeners;
  }
}

export const activityManager = new ActivityService();

export const recordUserActivity = (activityType: string) => {
  activityManager.recordActivity(activityType);
};
